/**
 * Transición “mazo → slot → pantalla completa” (servicios / tarjetas).
 * Parte 1: del icono mazo al rect del slot.
 * Parte 2: la carta alterna cara y tapa (rotateY) mientras crece hasta cubrir el viewport; termina en la cara (contenido).
 */

const DEFAULT_TAPA_URL = '/servicios/tarjeta-trabajo-plantilla.webp';

export function prefersReducedMotionTarjeta() {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function escUrl(u) {
  return String(u || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * @param {object} opts
 * @param {HTMLElement | null | undefined} opts.deckEl
 * @param {HTMLElement} opts.targetEl
 * @param {boolean} opts.skipPart1
 * @param {string} opts.imageUrl cara (contenido / miniatura)
 * @param {string} [opts.tapaImageUrl] reverso; por defecto plantilla del mazo
 * @returns {Promise<void>}
 */
export function runTarjetaDeckOpenTransition({ deckEl, targetEl, skipPart1, imageUrl, tapaImageUrl }) {
  if (prefersReducedMotionTarjeta() || !targetEl || !imageUrl) {
    return Promise.resolve();
  }

  const tapaUrl = tapaImageUrl || DEFAULT_TAPA_URL;

  return new Promise((resolve) => {
    document.getElementById('mlv-tarjeta-transition-root')?.remove();

    const slot = targetEl.getBoundingClientRect();
    if (slot.width < 8 || slot.height < 8) {
      resolve();
      return;
    }

    const deckR = deckEl?.getBoundingClientRect?.();
    const deckCX = deckR ? deckR.left + deckR.width / 2 : window.innerWidth - 40;
    const deckCY = deckR ? deckR.top + deckR.height / 2 : 96;
    const tgtCX = slot.left + slot.width / 2;
    const tgtCY = slot.top + slot.height / 2;
    const w = slot.width;
    const h = slot.height;

    const root = document.createElement('div');
    root.id = 'mlv-tarjeta-transition-root';
    root.className = 'fixed inset-0 z-[400] pointer-events-none';
    root.style.perspective = '1400px';
    root.setAttribute('aria-hidden', 'true');

    const outer = document.createElement('div');
    outer.className =
      'absolute rounded-xl border border-amber-500/45 shadow-2xl shadow-black/60 ring-1 ring-black/50';
    outer.style.willChange = 'left, top, width, height, opacity';
    outer.style.transformStyle = 'preserve-3d';

    const flipInner = document.createElement('div');
    flipInner.style.cssText =
      'width:100%;height:100%;position:relative;transform-style:preserve-3d;will-change:transform;border-radius:inherit';

    const faceFront = document.createElement('div');
    faceFront.style.cssText = [
      'position:absolute',
      'inset:0',
      'border-radius:inherit',
      'backface-visibility:hidden',
      '-webkit-backface-visibility:hidden',
      'transform:rotateY(0)',
      `background-image:url("${escUrl(imageUrl)}")`,
      'background-size:cover',
      'background-position:center',
    ].join(';');

    const faceBack = document.createElement('div');
    const mismaCara =
      String(imageUrl).replace(/\?.*$/, '') === String(tapaUrl).replace(/\?.*$/, '');
    const backBg = mismaCara
      ? 'background-image:none;background:linear-gradient(155deg,#120e0a 0%,#2a2218 40%,#1a1510 100%)'
      : `background-image:url("${escUrl(tapaUrl)}");background-size:cover;background-position:center`;
    faceBack.style.cssText = [
      'position:absolute',
      'inset:0',
      'border-radius:inherit',
      'backface-visibility:hidden',
      '-webkit-backface-visibility:hidden',
      'transform:rotateY(180deg)',
      backBg,
    ].join(';');

    flipInner.appendChild(faceFront);
    flipInner.appendChild(faceBack);
    outer.appendChild(flipInner);
    root.appendChild(outer);
    document.body.appendChild(root);

    const s0 = skipPart1 ? 1 : 0.24;
    const w0 = w * s0;
    const h0 = h * s0;
    const x0 = skipPart1 ? tgtCX : deckCX;
    const y0 = skipPart1 ? tgtCY : deckCY;
    const rotZ0 = skipPart1 ? 0 : -16;

    outer.style.left = `${x0 - w0 / 2}px`;
    outer.style.top = `${y0 - h0 / 2}px`;
    outer.style.width = `${w0}px`;
    outer.style.height = `${h0}px`;
    flipInner.style.transform = `rotateZ(${rotZ0}deg) rotateY(0deg)`;

    const k1 = [
      {
        left: `${x0 - w0 / 2}px`,
        top: `${y0 - h0 / 2}px`,
        width: `${w0}px`,
        height: `${h0}px`,
      },
      {
        left: `${slot.left}px`,
        top: `${slot.top}px`,
        width: `${w}px`,
        height: `${h}px`,
      },
    ];

    const anim1 = outer.animate(skipPart1 ? [k1[1], k1[1]] : k1, {
      duration: skipPart1 ? 0 : 600,
      easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
      fill: 'forwards',
    });

    const innerK1 = [
      { transform: `rotateZ(${rotZ0}deg) rotateY(0deg)` },
      { transform: 'rotateZ(0deg) rotateY(0deg)' },
    ];
    flipInner.animate(skipPart1 ? [innerK1[1], innerK1[1]] : innerK1, {
      duration: skipPart1 ? 0 : 600,
      easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
      fill: 'forwards',
    });

    anim1.finished
      .then(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const scale = Math.max(vw / w, vh / h) * 1.06;
        const w2 = w * scale;
        const h2 = h * scale;
        const left2 = vw / 2 - w2 / 2;
        const top2 = vh / 2 - h2 / 2;

        const vueltasCompletas = 4;
        const yEnd = vueltasCompletas * 360;

        const dur2 = 950;
        const anim2 = outer.animate(
          [
            { left: `${slot.left}px`, top: `${slot.top}px`, width: `${w}px`, height: `${h}px`, opacity: 1 },
            { left: `${left2}px`, top: `${top2}px`, width: `${w2}px`, height: `${h2}px`, opacity: 1 },
          ],
          { duration: dur2, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'forwards' }
        );

        const animFlip = flipInner.animate(
          [{ transform: 'rotateZ(0deg) rotateY(0deg)' }, { transform: `rotateZ(0deg) rotateY(${yEnd}deg)` }],
          { duration: dur2, easing: 'linear', fill: 'forwards' }
        );

        return Promise.all([anim2.finished, animFlip.finished]);
      })
      .then(() => {
        root.remove();
        resolve();
      })
      .catch(() => {
        root.remove();
        resolve();
      });
  });
}
